import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft, Award, Activity, Shield, FileText, AlertTriangle,
  CheckCircle2, XCircle, RotateCcw, TrendingUp, Info, User, Clock,
  ChevronDown, ChevronUp
} from 'lucide-react';
import SaakhSetuLoader from '@/components/SaakhSetuLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OVERRIDE_REASONS = [
  { code: 'FIELD_KNOWLEDGE', label: 'Field Knowledge / Ground Truth' },
  { code: 'DOCUMENT_VERIFICATION', label: 'Document Verification' },
  { code: 'POLICY_EXCEPTION', label: 'Policy Exception' },
  { code: 'OTHER', label: 'Other' },
];

const LENDER_DECISIONS = [
  { code: 'APPROVE', label: 'Approve', color: '#16a34a' },
  { code: 'REJECT', label: 'Reject', color: '#dc2626' },
  // Hold / Refer to Senior are valid lender decision values but are NOT yet wired
  // to dedicated workflow states (both currently map to the same assessment
  // transition). Hidden from the demo build to keep behaviour predictable.
];

const STATUS_BADGE = {
  SUBMITTED: { bg: '#fde68a', color: '#92400e', label: 'Submitted - Awaiting Score' },
  REVIEW_PENDING: { bg: '#fef9c3', color: '#854d0e', label: 'Pending Review' },
  APPROVED: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
  REJECTED: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  RETURNED: { bg: '#e0e7ff', color: '#3730a3', label: 'Returned' },
  SCORED: { bg: '#f0f9ff', color: '#075985', label: 'Scored' },
};

const OfficerReview = ({ user }) => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [scoring, setScoring] = useState(false);

  // Hard RBAC guard (defense-in-depth in addition to route-level guard in App.js)
  useEffect(() => {
    if (user && user.role !== 'Credit Officer' && user.role !== 'System Administrator') {
      toast.error('Access denied. Only Officers can view Assessment Review.');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Review form state
  const [reviewNotes, setReviewNotes] = useState('');
  const [overrideFlag, setOverrideFlag] = useState(false);
  const [overrideReasonCode, setOverrideReasonCode] = useState('');
  const [overrideNotes, setOverrideNotes] = useState('');
  const [lenderDecision, setLenderDecision] = useState('APPROVE');
  const [sanctionedLimitFinal, setSanctionedLimitFinal] = useState('');
  const [expandedSections, setExpandedSections] = useState({ modelTrace: true, policyTrace: true });

  const fetchData = useCallback(async () => {
    try {
      const aRes = await axios.get(`${API}/assessments/${assessmentId}`);
      setAssessment(aRes.data);
      const fRes = await axios.get(`${API}/farmers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const f = fRes.data.find(fa => fa.id === aRes.data.farmer_id);
      setFarmer(f || null);
    } catch {
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      const payload = {
        action,
        review_notes: reviewNotes || null,
        override_flag: overrideFlag,
        override_reason_code: overrideFlag ? overrideReasonCode : null,
        override_notes: overrideFlag ? overrideNotes : null,
        final_lender_decision: lenderDecision,
        sanctioned_limit_final: sanctionedLimitFinal ? Number(sanctionedLimitFinal) : null,
      };
      const res = await axios.post(`${API}/assessments/${assessmentId}/review`, payload);
      setAssessment(res.data);
      const labels = { APPROVE: 'approved', REJECT: 'rejected', RETURN: 'returned for revision' };
      toast.success(`Assessment ${labels[action] || action.toLowerCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendForReview = async () => {
    try {
      const res = await axios.post(`${API}/assessments/${assessmentId}/send-for-review`);
      setAssessment(res.data);
      toast.success('Sent for officer review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send for review');
    }
  };

  const handleGenerateScore = async () => {
    setScoring(true);
    try {
      // Refresh readiness first to get latest diagnostics
      await axios.post(`${API}/assessments/${assessmentId}/refresh-readiness`);
      const res = await axios.post(`${API}/assessments/${assessmentId}/submit`);
      setAssessment(res.data);
      if (res.data.assessment_status === 'SCORED') {
        toast.success(`Yogyank generated: ${res.data.yogyank_score_base ?? res.data.yogyank_score_final} (${res.data.yogyank_score_band})`);
      } else {
        toast.success('Scoring complete');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.reasons) {
        toast.error(`Cannot score: ${detail.reasons.slice(0, 3).join(', ')}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Score generation failed');
      }
    } finally {
      setScoring(false);
    }
  };

  const toggleSection = (key) => setExpandedSections(p => ({ ...p, [key]: !p[key] }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <SaakhSetuLoader message="Loading review..." />
      </div>
    );
  }

  if (!assessment) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Assessment not found</div>;
  }

  const modelTrace = assessment.model_trace || {};
  const policyTrace = assessment.policy_trace || {};
  const reviewMeta = assessment.review_metadata || {};
  const statusBadge = STATUS_BADGE[assessment.assessment_status] || STATUS_BADGE.SCORED;
  const isPending = assessment.assessment_status === 'REVIEW_PENDING';
  const isScored = assessment.assessment_status === 'SCORED';
  const isSubmitted = assessment.assessment_status === 'SUBMITTED';
  const isFinalized = ['APPROVED', 'REJECTED'].includes(assessment.assessment_status);
  const isReturned = assessment.assessment_status === 'RETURNED';
  const hasScore = (assessment.yogyank_score_base ?? assessment.yogyank_score_final) !== null && (assessment.yogyank_score_base ?? assessment.yogyank_score_final) !== undefined;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      {/* Navbar */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: '36px' }} />
              <Badge variant="outline" className="text-xs">Officer Review</Badge>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="review-page-title">
              Assessment Review
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-500">{farmer?.farmer_name || 'Unknown Farmer'}</span>
              <Badge className="text-[10px]" style={{ background: statusBadge.bg, color: statusBadge.color, border: `1px solid ${statusBadge.color}30` }}>
                {statusBadge.label}
              </Badge>
              <span className="text-xs text-gray-400">Cycle #{assessment.cycle_number}</span>
            </div>
          </div>
          {isSubmitted && (
            <Button
              onClick={handleGenerateScore}
              disabled={scoring}
              style={{ background: '#7c3aed' }}
              data-testid="generate-score-btn"
            >
              {scoring ? 'Generating score...' : 'Generate Score (Yogyank)'}
            </Button>
          )}
          {isScored && (
            <Button onClick={handleSendForReview} style={{ background: '#2563eb' }} data-testid="send-for-review-btn">
              Send for Review
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Score + Traces (READ-ONLY) */}
          <div className="lg:col-span-2 space-y-5">
            {/* Pre-Scoring Banner for SUBMITTED assessments */}
            {isSubmitted && !hasScore && (
              <Card className="border-2 border-purple-300 bg-purple-50" data-testid="pre-score-banner">
                <CardContent className="pt-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-purple-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-purple-900 text-sm">Awaiting Officer Scoring</div>
                      <div className="text-xs text-purple-800 mt-1">
                        This assessment was sent by a Field Agent. Review the farmer data below, then click
                        <strong> Generate Score </strong> above to run the Yogyank scoring pipeline before approval.
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 3-Panel Decision Readiness Hero — separation of concerns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="decision-readiness-hero">

              {/* Panel 1 — Base Entitlement Score (Yogyank, bank-agnostic) */}
              <Card className="overflow-hidden border-2" style={{ borderColor: '#bfdbfe' }} data-testid="entitlement-score-card">
                <div className="h-1" style={{ background: (assessment.yogyank_score_base || 0) >= 500 ? '#16a34a' : (assessment.yogyank_score_base || 0) >= 300 ? '#ca8a04' : (assessment.yogyank_score_base ? '#dc2626' : '#94a3b8') }} />
                <CardContent className="pt-4 pb-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-700">Base Score (Yogyank)</div>
                      <div className="text-[10px] text-gray-400">Bank-agnostic model output</div>
                    </div>
                    <div className="text-[9px] text-gray-400 font-mono text-right">
                      {assessment.model_version?.slice(0, 16) || 'yogyank-xgb-v1.0'}
                    </div>
                  </div>
                  <div className="text-4xl font-bold leading-none" style={{ color: (assessment.yogyank_score_base || 0) >= 500 ? '#16a34a' : (assessment.yogyank_score_base || 0) >= 300 ? '#ca8a04' : (assessment.yogyank_score_base ? '#dc2626' : '#94a3b8') }}>
                    {assessment.yogyank_score_base ?? 'N/A'}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    Independent of lender policy
                  </div>
                  <div className="text-[9px] text-gray-400">
                    Scored: {assessment.scoring_date ? new Date(assessment.scoring_date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                  </div>
                </CardContent>
              </Card>

              {/* Panel 2 — Policy Band (lender configuration layer) */}
              <Card className="overflow-hidden border-2" style={{ borderColor: '#e9d5ff' }} data-testid="policy-band-card">
                <div className="h-1 bg-purple-600" />
                <CardContent className="pt-4 pb-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-purple-700">Policy Band</div>
                      <div className="text-[10px] text-gray-400">Lender configuration result</div>
                    </div>
                    <div className="text-[9px] text-gray-400 font-mono text-right">
                      {(assessment.policy_version || 'v1.0').split('_')[0]}
                    </div>
                  </div>
                  <div className="text-xl font-bold leading-tight text-purple-800">
                    {policyTrace.policy_band || 'N/A'}
                  </div>
                  <div className="text-[10px] text-gray-600">
                    Adjustment <span className={`font-mono font-semibold ${(policyTrace.policy_adjustment_points || 0) > 0 ? 'text-green-700' : (policyTrace.policy_adjustment_points || 0) < 0 ? 'text-red-700' : 'text-gray-700'}`}>{(policyTrace.policy_adjustment_points || 0) > 0 ? '+' : ''}{policyTrace.policy_adjustment_points || 0}</span>
                    {assessment.yogyank_score_final != null && (
                      <span className="text-gray-500"> · Post-policy <span className="font-mono font-semibold text-gray-800">{assessment.yogyank_score_final}</span></span>
                    )}
                  </div>
                  {(policyTrace.policy_reasons || []).length > 0 && (
                    <div className="text-[10px] text-gray-500 line-clamp-2">
                      {(policyTrace.policy_reasons || [])[0]}
                    </div>
                  )}
                  <div className="text-[9px] text-gray-400">Configurable per bank · does not alter base score</div>
                </CardContent>
              </Card>

              {/* Panel 3 — Lender Decision (officer workflow layer) */}
              <Card className="overflow-hidden border-2" style={{ borderColor: '#fde68a' }} data-testid="lender-decision-card">
                <div className="h-1 bg-amber-500" />
                <CardContent className="pt-4 pb-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">Lender Decision</div>
                      <div className="text-[10px] text-gray-400">Officer workflow action</div>
                    </div>
                    {reviewMeta.override_flag && (
                      <Badge className="text-[9px] border-none bg-amber-100 text-amber-800">
                        <Shield className="h-2.5 w-2.5 mr-0.5" />Override
                      </Badge>
                    )}
                  </div>
                  {reviewMeta.final_lender_decision ? (
                    <>
                      <div className="text-xl font-bold leading-tight" style={{
                        color: reviewMeta.final_lender_decision === 'APPROVE' ? '#16a34a'
                          : reviewMeta.final_lender_decision === 'REJECT' ? '#dc2626'
                          : '#b45309'
                      }}>
                        {reviewMeta.final_lender_decision}
                      </div>
                      {reviewMeta.sanctioned_limit_final != null && (
                        <div className="text-[10px] text-gray-600">
                          Sanctioned: <span className="font-mono font-bold">Rs. {Number(reviewMeta.sanctioned_limit_final).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      <div className="text-[9px] text-gray-400">
                        {reviewMeta.reviewer_email?.split('@')[0] || 'System'} · {reviewMeta.reviewed_at ? new Date(reviewMeta.reviewed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                      </div>
                    </>
                  ) : isPending ? (
                    <>
                      <div className="text-lg font-semibold text-amber-700 leading-tight flex items-center gap-1.5">
                        <AlertTriangle className="h-4 w-4" /> Awaiting Decision
                      </div>
                      <div className="text-[10px] text-gray-500">Decide via form on the right</div>
                      <div className="text-[9px] text-gray-400">Does not modify score or policy</div>
                    </>
                  ) : (
                    <>
                      <div className="text-lg font-semibold text-gray-400 leading-tight">Not yet decided</div>
                      <div className="text-[10px] text-gray-500">Generate score & send for review first</div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Model Trace (collapsible) */}
            <Card data-testid="model-trace-card">
              <CardContent className="pt-4">
                <button className="w-full flex items-center justify-between" onClick={() => toggleSection('modelTrace')}>
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Model Trace</span>
                    <Badge className="text-[10px] bg-blue-50 text-blue-700">Read-only</Badge>
                  </div>
                  {expandedSections.modelTrace ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </button>
                {expandedSections.modelTrace && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                      {[
                        ['Model Version', modelTrace.model_version],
                        ['Pipeline Version', modelTrace.pipeline_version],
                        ['Completeness', `${modelTrace.completeness_pct || 'N/A'}%`],
                        ['Status', modelTrace.scoring_status],
                      ].map(([label, val]) => (
                        <div key={label} className="flex justify-between py-1 border-b border-gray-50">
                          <span className="text-xs text-gray-500">{label}</span>
                          <span className="text-xs font-medium text-gray-900 font-mono">{val || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                    {(modelTrace.entitlement_reasons || []).length > 0 && (
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Top Drivers</div>
                        {modelTrace.entitlement_reasons.map((r, i) => (
                          <div key={i} className="text-xs text-gray-700 flex items-start gap-1.5 py-0.5">
                            <TrendingUp className="h-3 w-3 mt-0.5 text-blue-500 shrink-0" /> {r}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Policy Trace (collapsible) */}
            <Card data-testid="policy-trace-card">
              <CardContent className="pt-4">
                <button className="w-full flex items-center justify-between" onClick={() => toggleSection('policyTrace')}>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-purple-600" />
                    <span className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Policy Trace</span>
                    <Badge className="text-[10px] bg-purple-50 text-purple-700">Read-only</Badge>
                  </div>
                  {expandedSections.policyTrace ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </button>
                {expandedSections.policyTrace && (
                  <div className="mt-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                      {[
                        ['Policy Version', policyTrace.policy_version],
                        ['Policy Band', policyTrace.policy_band],
                        ['Adjustment', `${(policyTrace.policy_adjustment_points || 0) >= 0 ? '+' : ''}${policyTrace.policy_adjustment_points || 0}`],
                      ].map(([label, val]) => (
                        <div key={label} className="flex justify-between py-1 border-b border-gray-50">
                          <span className="text-xs text-gray-500">{label}</span>
                          <span className="text-xs font-medium text-gray-900 font-mono">{val || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                    {(policyTrace.policy_reasons || []).length > 0 && (
                      <div className="mt-2">
                        {policyTrace.policy_reasons.map((r, i) => (
                          <div key={i} className="text-xs text-gray-700 flex items-start gap-1.5 py-0.5">
                            <Info className="h-3 w-3 mt-0.5 text-purple-500 shrink-0" /> {r}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assessment Details */}
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">Assessment Details</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    ['Journey Type', assessment.journey_type?.replace(/_/g, ' ')],
                    ['Cycle', `#${assessment.cycle_number}`],
                    ['Previous Cycle', assessment.previous_cycle_reference || 'N/A'],
                    ['Assessment Version', assessment.assessment_version],
                    ['Model Version', assessment.model_version],
                    ['Policy Version', assessment.policy_version],
                    ['Pipeline Version', assessment.pipeline_version],
                    ['Prev. Sanctioned', assessment.sanctioned_limit_previous ? `Rs. ${Number(assessment.sanctioned_limit_previous).toLocaleString()}` : 'N/A'],
                    ['Requested Limit', assessment.sanctioned_limit_requested ? `Rs. ${Number(assessment.sanctioned_limit_requested).toLocaleString()}` : 'Same as previous'],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between py-1 border-b border-gray-50">
                      <span className="text-xs text-gray-500">{label}</span>
                      <span className="text-xs font-medium text-gray-900 font-mono">{val || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Review History */}
            {(reviewMeta.review_history || []).length > 0 && (
              <Card data-testid="review-history-card">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-gray-600" />
                    <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Review History</span>
                  </div>
                  <div className="space-y-2">
                    {reviewMeta.review_history.map((entry, i) => (
                      <div key={i} className="flex items-start gap-3 p-2.5 rounded-md bg-gray-50 border border-gray-100">
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                          <User className="h-3 w-3 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-700">{entry.action}</span>
                            <span className="text-[10px] text-gray-400">{new Date(entry.timestamp).toLocaleString()}</span>
                            {entry.override_flag && <Badge className="text-[10px] bg-amber-100 text-amber-800">Override</Badge>}
                          </div>
                          {entry.notes && <div className="text-xs text-gray-600 mt-0.5">{entry.notes}</div>}
                          {entry.final_lender_decision && (
                            <div className="text-xs text-gray-500 mt-0.5">Decision: <span className="font-semibold">{entry.final_lender_decision}</span></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column: Review Actions */}
          <div className="space-y-5">
            {/* Final Status Card */}
            {(isFinalized || isReturned) && (
              <Card className={`border-2 ${assessment.assessment_status === 'APPROVED' ? 'border-green-400' : assessment.assessment_status === 'REJECTED' ? 'border-red-400' : 'border-blue-400'}`} data-testid="final-status-card">
                <CardContent className="pt-4 text-center">
                  {assessment.assessment_status === 'APPROVED' && <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-2" />}
                  {assessment.assessment_status === 'REJECTED' && <XCircle className="h-10 w-10 text-red-600 mx-auto mb-2" />}
                  {assessment.assessment_status === 'RETURNED' && <RotateCcw className="h-10 w-10 text-blue-600 mx-auto mb-2" />}
                  <div className="text-lg font-bold" style={{ color: statusBadge.color }}>{statusBadge.label}</div>
                  {reviewMeta.final_lender_decision && (
                    <div className="text-xs text-gray-500 mt-1">
                      Lender Decision: <span className="font-bold">{reviewMeta.final_lender_decision}</span>
                    </div>
                  )}
                  {reviewMeta.sanctioned_limit_final && (
                    <div className="text-sm font-bold text-gray-900 mt-1">
                      Final Limit: Rs. {Number(reviewMeta.sanctioned_limit_final).toLocaleString()}
                    </div>
                  )}
                  {reviewMeta.reviewed_at && (
                    <div className="text-[10px] text-gray-400 mt-1">{new Date(reviewMeta.reviewed_at).toLocaleString()}</div>
                  )}
                  {reviewMeta.override_flag && (
                    <Badge className="mt-2 text-[10px] bg-amber-100 text-amber-800">
                      Override: {reviewMeta.override_reason_code}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Review Action Form (only when REVIEW_PENDING) */}
            {isPending && (
              <Card className="border-2 border-blue-300" data-testid="review-action-form">
                <CardContent className="pt-4 space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Officer Review</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Decide · set limit · note observations</div>
                  </div>

                  {/* Lender Decision */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Final Lender Decision</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {LENDER_DECISIONS.map(d => (
                        <button
                          key={d.code}
                          className={`p-2 rounded-md border text-xs font-medium transition-all ${lenderDecision === d.code ? 'ring-2 ring-offset-1' : 'hover:bg-gray-50'}`}
                          style={lenderDecision === d.code ? { borderColor: d.color, color: d.color, ringColor: d.color } : {}}
                          onClick={() => setLenderDecision(d.code)}
                          data-testid={`decision-${d.code}`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sanctioned Limit */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Sanctioned Limit (Final)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-xs text-gray-500">Rs.</span>
                      <Input
                        type="number"
                        value={sanctionedLimitFinal}
                        onChange={e => setSanctionedLimitFinal(e.target.value)}
                        className="pl-10 text-sm"
                        placeholder={assessment.sanctioned_limit_requested ? String(assessment.sanctioned_limit_requested) : 'Enter amount'}
                        data-testid="sanctioned-limit-input"
                      />
                    </div>
                  </div>

                  {/* Review Notes */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Review Notes</Label>
                    <Textarea
                      value={reviewNotes}
                      onChange={e => setReviewNotes(e.target.value)}
                      rows={3}
                      placeholder="Enter review observations..."
                      className="text-sm"
                      data-testid="review-notes-input"
                    />
                  </div>

                  {/* Primary Action Buttons */}
                  <div className="space-y-2 pt-1 border-t border-gray-100">
                    <Button
                      className="w-full"
                      style={{ background: '#16a34a' }}
                      disabled={submitting}
                      onClick={() => handleAction('APPROVE')}
                      data-testid="approve-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        disabled={submitting}
                        onClick={() => handleAction('REJECT')}
                        data-testid="reject-btn"
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Reject
                      </Button>
                      <Button
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        disabled={submitting}
                        onClick={() => handleAction('RETURN')}
                        data-testid="return-btn"
                      >
                        <RotateCcw className="h-4 w-4 mr-1" /> Return
                      </Button>
                    </div>
                  </div>

                  {/* ADVANCED: Score Override (visually separated to not compete with primary flow) */}
                  <div className="pt-3 mt-2 border-t border-dashed border-amber-300">
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-2">Advanced</div>
                    <div className="space-y-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <div>
                            <Label className="text-xs font-semibold text-amber-800 leading-tight block">Score Override</Label>
                            <div className="text-[10px] text-amber-700/80 leading-tight">Audited; base score unchanged</div>
                          </div>
                        </div>
                        <Switch checked={overrideFlag} onCheckedChange={setOverrideFlag} data-testid="override-toggle" />
                      </div>
                      {overrideFlag && (
                        <div className="space-y-2 mt-2">
                          <div className="space-y-1">
                            <Label className="text-[10px] text-amber-700">Override Reason</Label>
                            <select
                              className="w-full border border-amber-300 rounded-md px-2 py-1.5 text-xs bg-white"
                              value={overrideReasonCode}
                              onChange={e => setOverrideReasonCode(e.target.value)}
                              data-testid="override-reason-select"
                            >
                              <option value="">Select reason...</option>
                              {OVERRIDE_REASONS.map(r => (
                                <option key={r.code} value={r.code}>{r.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] text-amber-700">Override Notes</Label>
                            <Textarea
                              value={overrideNotes}
                              onChange={e => setOverrideNotes(e.target.value)}
                              rows={2}
                              className="text-xs"
                              placeholder="Justify the override..."
                              data-testid="override-notes-input"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Readiness Summary */}
            <Card>
              <CardContent className="pt-4 space-y-2">
                <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Readiness</div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-gray-600">Score Readiness</span>
                  <Badge className={`text-[10px] ${assessment.score_readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {assessment.score_readiness_status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-gray-600">Policy Readiness</span>
                  <Badge className={`text-[10px] ${assessment.policy_readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                    {assessment.policy_readiness_status}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Document Summary */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-gray-600" />
                  <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Documents</span>
                </div>
                <div className="flex gap-3 text-xs">
                  <span className="text-green-700">Valid: {assessment.document_status?.valid || 0}</span>
                  <span className="text-red-700">Expired: {assessment.document_status?.expired || 0}</span>
                  <span className="text-gray-500">Missing: {assessment.document_status?.missing || 0}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OfficerReview;
